import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Send,
  X,
  XCircle,
  FileText
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import respuestaService from '../../services/respuestaService';
import usuarioExamenService from '../../services/usuarioExamenService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import AuthImage from '../../components/common/AuthImage';
import PantallaEjemplosRaven from '../../components/exam/PantallaEjemplosRaven';

/**
 * Componente para realizar el Test Raven
 * Formato: Todas las preguntas visibles en lista
 * Layout: Imagen de pregunta a la izquierda, 8 opciones en cuadrados a la derecha
 */
const RealizarExamenRaven = ({ examen, perfil, proceso, onFinalizar }) => {
  const { user } = useAuthStore();
  const tiempoInicial = examen.tiempoMinutos > 0 ? examen.tiempoMinutos * 60 : null;
  
  const [tiempoRestante, setTiempoRestante] = useState(tiempoInicial);
  const [respuestas, setRespuestas] = useState({});
  const [showPrevisualizacion, setShowPrevisualizacion] = useState(false);
  const [examenIniciado, setExamenIniciado] = useState(false);
  const [mostrarPantallasInicio, setMostrarPantallasInicio] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [guardandoProgreso, setGuardandoProgreso] = useState(false);
  const [estadoUsuarioExamen, setEstadoUsuarioExamen] = useState(null);
  const [cargandoEstado, setCargandoEstado] = useState(true);

  // Temporizador
  useEffect(() => {
    if (!examenIniciado || tiempoRestante === null) return;

    if (tiempoRestante <= 0) {
      handleTiempoAgotado();
      return;
    }

    const timer = setInterval(() => {
      setTiempoRestante(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [tiempoRestante, examenIniciado]);

  // Verificar estado del examen
  useEffect(() => {
    verificarEstadoExamen();
  }, []);

  // Cargar progreso existente
  useEffect(() => {
    if (examenIniciado && examen.preguntas.length > 0) {
      cargarProgresoExistente();
    }
  }, [examenIniciado, examen]);

  // Debug: Verificar URLs de imágenes (solo una vez cuando carga el examen)
  useEffect(() => {
    if (examen.preguntas && examen.preguntas.length > 0) {
      console.log('=== URLs de Imágenes del Examen Raven ===');
      examen.preguntas.forEach((pregunta, index) => {
        console.log(`Pregunta ${index + 1}:`, pregunta.imagen);
        if (pregunta.opciones) {
          pregunta.opciones.forEach((opcion, i) => {
            console.log(`  Opción ${i + 1}:`, opcion.imagen);
          });
        }
      });
      console.log('========================================');
    }
  }, [examen.id]); // Solo cuando cambia el ID del examen

  const verificarEstadoExamen = async () => {
    try {
      setCargandoEstado(true);
      const progreso = await usuarioExamenService.obtenerProgreso(user.id, examen.id);
      setEstadoUsuarioExamen(progreso);
      
      if (progreso && (progreso.estado === 'FINALIZADO' || progreso.estado === 'COMPLETADO')) {
        toast.info('Este examen ya fue completado anteriormente');
        setTimeout(() => {
          onFinalizar();
        }, 2000);
      }
    } catch (error) {
      console.error('Error al verificar estado del examen:', error);
      setEstadoUsuarioExamen(null);
    } finally {
      setCargandoEstado(false);
    }
  };

  const cargarProgresoExistente = async () => {
    try {
      const progreso = await respuestaService.obtenerProgresoExamen(user.id, examen.id);
      if (progreso && progreso.length > 0) {
        const respuestasGuardadas = {};
        progreso.forEach(item => {
          if (item.opcionId !== null) {
            respuestasGuardadas[item.preguntaId] = item.opcionId;
          }
        });
        setRespuestas(respuestasGuardadas);
      }
    } catch (error) {
      console.error('Error al cargar progreso existente:', error);
    }
  };

  const formatearTiempo = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
  };

  const handleSeleccionarRespuesta = async (preguntaId, opcionId) => {
    // Guardar posición actual del scroll
    const scrollPosition = window.scrollY;
    
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: opcionId
    }));

    // Guardar progreso automáticamente
    setGuardandoProgreso(true);
    try {
      await respuestaService.guardarProgresoExamen({
        usuarioId: user.id,
        examenId: examen.id,
        preguntaId: preguntaId,
        opcionId: opcionId
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    } finally {
      setGuardandoProgreso(false);
      // Restaurar posición del scroll después de actualizar
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    }
  };

  const handleTiempoAgotado = async () => {
    try {
      toast.error('⏰ Se ha agotado el tiempo del examen');
      await usuarioExamenService.tiempoAgotado(user.id, examen.id);
    } catch (error) {
      console.error('⚠️ Error al marcar tiempo agotado:', error);
    }
    await handleEnviarExamen();
  };

  const handleEnviarExamen = async () => {
    try {
      setEnviando(true);
      
      const respuestasArray = Object.entries(respuestas).map(([preguntaId, opcionId]) => {
        const pregunta = examen.preguntas.find(p => p.id === parseInt(preguntaId));
        const opcion = pregunta?.opciones?.find(o => o.id === opcionId);
        
        return {
          usuarioId: user.id,
          examenId: examen.id,
          preguntaId: parseInt(preguntaId),
          opcionId: opcionId,
          correcta: opcion?.esCorrecta || false
        };
      });

      await respuestaService.enviarRespuestasExamen(respuestasArray);

      const respuestasCorrectas = respuestasArray.filter(r => r.correcta).length;
      const totalPreguntas = examen.preguntas.length;
      const puntajeMaximo = examen.preguntas.reduce((sum, p) => sum + (p.peso || 1), 0);
      const puntajeObtenido = examen.preguntas
        .filter(p => respuestasArray.find(r => r.preguntaId === p.id && r.correcta))
        .reduce((sum, p) => sum + (p.peso || 1), 0);

      try {
        await usuarioExamenService.finalizarExamen(
          user.id, 
          examen.id, 
          puntajeObtenido, 
          puntajeMaximo,
          `${respuestasCorrectas}/${totalPreguntas} respuestas correctas`
        );
      } catch (errorEstado) {
        console.error('⚠️ Error al cambiar estado del usuario-examen:', errorEstado);
      }

      toast.success('✅ Examen enviado correctamente');
      setTimeout(() => {
        onFinalizar();
      }, 1500);
    } catch (error) {
      console.error('Error al enviar examen:', error);
      toast.error('Error al enviar el examen. Por favor intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const getPreguntasRespondidas = () => {
    return Object.keys(respuestas).length;
  };

  const handleIniciarExamen = async () => {
    try {
      await usuarioExamenService.iniciarExamen(user.id, examen.id);
      console.log('✅ Estado del usuario-examen cambiado a EN_PROGRESO');
    } catch (error) {
      console.error('⚠️ Error al cambiar estado del usuario-examen:', error);
    }
    setMostrarPantallasInicio(false);
    setExamenIniciado(true);
  };

  // Loader inicial
  if (cargandoEstado) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Verificando estado del examen...</p>
        </div>
      </div>
    );
  }

  // Examen ya finalizado
  if (estadoUsuarioExamen && (estadoUsuarioExamen.estado === 'FINALIZADO' || estadoUsuarioExamen.estado === 'COMPLETADO')) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              Examen Finalizado
            </h1>
            <p className="text-neutral-600 mb-4">
              Ya has completado este examen anteriormente
            </p>
            
            {estadoUsuarioExamen.puntajeObtenido !== null && (
              <div className="bg-neutral-50 rounded-lg p-6 max-w-md mx-auto mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-neutral-600 mb-1">Puntaje Obtenido</p>
                    <p className="text-2xl font-bold text-primary">
                      {estadoUsuarioExamen.puntajeObtenido}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 mb-1">Puntaje Máximo</p>
                    <p className="text-2xl font-bold text-neutral-900">
                      {estadoUsuarioExamen.puntajeMaximo}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <p className="text-sm text-neutral-600 mb-1">Porcentaje</p>
                  <p className="text-3xl font-bold text-success">
                    {((estadoUsuarioExamen.puntajeObtenido / estadoUsuarioExamen.puntajeMaximo) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            )}

            <Button variant="primary" onClick={onFinalizar}>
              Volver a Mis Exámenes
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Pantallas de ejemplos e instrucciones para Raven
  if (mostrarPantallasInicio) {
    return (
      <PantallaEjemplosRaven
        onIniciar={handleIniciarExamen}
      />
    );
  }

  // Vista principal del examen - Formato Lista
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header fijo con información del examen - Responsive */}
      <div className="sticky top-0 z-10 bg-white shadow-md">
        <div className="max-w-[1920px] mx-auto px-3 sm:px-6 py-2 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            {/* Título y tiempo en la misma fila en móvil */}
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <div className="min-w-0 flex-1 sm:flex-none">
                <h1 className="text-base sm:text-xl font-bold text-neutral-900 truncate">{examen.nombre}</h1>
                <p className="text-xs sm:text-sm text-neutral-600 truncate">
                  {proceso.nombre} - {perfil.nombre}
                </p>
              </div>
              {/* Tiempo - siempre visible */}
              <div className="flex-shrink-0">
                {tiempoRestante !== null ? (
                  <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 rounded-lg ${
                    tiempoRestante < 300 ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'
                  }`}>
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm sm:text-lg font-mono font-bold">
                      {formatearTiempo(tiempoRestante)}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 rounded-lg bg-success/10 text-success">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-bold">Sin límite</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Badges y botón - Segunda fila en móvil */}
            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
              {guardandoProgreso && (
                <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 bg-info/10 text-info rounded-lg">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-info border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">Guardando...</span>
                </div>
              )}
              <Badge variant="secondary" className="text-xs sm:text-sm">
                {getPreguntasRespondidas()} / {examen.preguntas.length}
              </Badge>
              {/* Botón oculto en móvil - hay uno flotante abajo */}
              <Button
                variant="success"
                onClick={() => setShowPrevisualizacion(true)}
                disabled={getPreguntasRespondidas() === 0}
                className="hidden sm:flex"
              >
                <Send className="w-4 h-4" />
                Enviar Examen
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de todas las preguntas */}
      <div className="space-y-6 max-w-[1920px] mx-auto px-6 py-6">
        {examen.preguntas.map((pregunta, index) => {
          const respuestaSeleccionada = respuestas[pregunta.id];
          
          return (
            <motion.div
              key={pregunta.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`${respuestaSeleccionada ? 'ring-2 ring-success' : 'ring-1 ring-neutral-200'}`}>
                {/* Header de la pregunta */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-neutral-200">
                  <div className="flex items-center gap-3">
                    <Badge variant={respuestaSeleccionada ? 'success' : 'primary'}>
                      Pregunta {index + 1}
                    </Badge>
                    {respuestaSeleccionada && (
                      <div className="flex items-center gap-1 text-success text-sm font-medium">
                        <CheckCircle className="w-4 h-4" />
                        Respondida
                      </div>
                    )}
                  </div>
                  {pregunta.peso && (
                    <Badge variant="secondary">
                      {pregunta.peso} puntos
                    </Badge>
                  )}
                </div>

                {/* Contenido de la pregunta - Layout: Imagen izquierda, Opciones derecha */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Lado izquierdo: Imagen de la pregunta */}
                  <div className="flex flex-col">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                      {pregunta.texto || `Pregunta ${index + 1}`}
                    </h3>
                    {pregunta.imagen && (
                      <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-xl p-6 border border-neutral-200 flex-1 flex items-center justify-center">
                        <AuthImage 
                          src={pregunta.imagen} 
                          alt={`Pregunta ${index + 1}`} 
                          className="w-full h-auto object-contain rounded-lg"
                        />
                      </div>
                    )}
                  </div>

                  {/* Lado derecho: Opciones en cuadrados - Responsive */}
                  <div className="flex flex-col">
                    <h4 className="text-base sm:text-lg font-semibold text-neutral-800 mb-3 sm:mb-4">
                      Selecciona una opción:
                    </h4>
                    {/* Grid responsive: 2 cols en móvil, 3 en tablet, 4 en desktop */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                      {pregunta.opciones && pregunta.opciones.slice(0, 8).map((opcion, opcionIndex) => {
                        const isSeleccionada = respuestaSeleccionada === opcion.id;
                        
                        return (
                          <motion.button
                            key={opcion.id}
                            onClick={() => handleSeleccionarRespuesta(pregunta.id, opcion.id)}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            className={`relative aspect-square rounded-lg sm:rounded-xl transition-all duration-200 overflow-hidden ${
                              isSeleccionada
                                ? 'border-3 sm:border-4 border-primary shadow-xl sm:shadow-2xl ring-2 sm:ring-4 ring-primary/30'
                                : 'border-2 border-neutral-300 bg-white hover:border-primary/50 hover:shadow-lg'
                            }`}
                          >
                            {/* Overlay de selección - Fondo azul semi-transparente */}
                            {isSeleccionada && (
                              <div className="absolute inset-0 bg-primary/20 z-0"></div>
                            )}

                            {/* Número de opción - Responsive */}
                            <div className={`absolute top-1 left-1 sm:top-2 sm:left-2 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-md z-10 ${
                              isSeleccionada 
                                ? 'bg-primary text-white' 
                                : 'bg-neutral-200 text-neutral-700'
                            }`}>
                              {opcionIndex + 1}
                            </div>

                            {/* Checkmark si está seleccionada - Responsive */}
                            {isSeleccionada && (
                              <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10">
                                <div className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center shadow-lg">
                                  <CheckCircle className="w-3 h-3 sm:w-6 sm:h-6 text-white fill-current" />
                                </div>
                              </div>
                            )}

                            {/* Imagen de la opción - Responsive padding */}
                            {opcion.imagen ? (
                              <div className="w-full h-full p-2 sm:p-3 flex items-center justify-center relative z-5">
                                <AuthImage 
                                  src={opcion.imagen} 
                                  alt={`Opción ${opcionIndex + 1}`}
                                  className="max-w-full max-h-full object-contain"
                                />
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center p-2 sm:p-3">
                                <span className={`text-sm sm:text-base text-center font-semibold ${
                                  isSeleccionada ? 'text-white' : 'text-neutral-700'
                                }`}>
                                  {opcion.texto || `Opción ${opcionIndex + 1}`}
                                </span>
                              </div>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Botón flotante para enviar */}
      <div className="fixed bottom-6 right-6 z-20">
        <Button
          variant="success"
          size="lg"
          onClick={() => setShowPrevisualizacion(true)}
          disabled={getPreguntasRespondidas() === 0}
          className="shadow-2xl"
        >
          <Send className="w-5 h-5" />
          Enviar Examen ({getPreguntasRespondidas()}/{examen.preguntas.length})
        </Button>
      </div>

      {/* Modal de Previsualización */}
      <Modal
        isOpen={showPrevisualizacion}
        onClose={() => setShowPrevisualizacion(false)}
        title="Revisión Final - Previsualiza tus respuestas"
        size="xl"
      >
        <div className="space-y-4">
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-warning mb-1">Revisa tus respuestas</p>
                <p className="text-sm text-neutral-700">
                  Una vez enviado el examen, no podrás modificar tus respuestas. Revisa cuidadosamente antes de confirmar.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-neutral-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Preguntas respondidas:</span>
              <span className="font-medium text-neutral-900">
                {getPreguntasRespondidas()} / {examen.preguntas.length}
              </span>
            </div>
            {tiempoRestante !== null && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Tiempo restante:</span>
                <span className="font-medium text-neutral-900">
                  {formatearTiempo(tiempoRestante)}
                </span>
              </div>
            )}
          </div>

          {getPreguntasRespondidas() < examen.preguntas.length ? (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-800 mb-1">
                    ⚠️ Atención: Tienes preguntas sin responder
                  </p>
                  <p className="text-sm text-red-700">
                    Faltan <strong>{examen.preguntas.length - getPreguntasRespondidas()} pregunta(s)</strong> por responder.
                    Puedes volver a revisar o enviar el examen con las respuestas actuales.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-green-800 mb-1">
                    ✓ Excelente: Todas las preguntas respondidas
                  </p>
                  <p className="text-sm text-green-700">
                    Has respondido todas las preguntas. Puedes confirmar el envío.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Lista de respuestas */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5" />
              Tus respuestas seleccionadas
            </h3>
            
            <div className="bg-neutral-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {examen.preguntas.map((pregunta, index) => {
                  const respuesta = respuestas[pregunta.id];
                  
                  return (
                    <div 
                      key={pregunta.id}
                      className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${
                        respuesta 
                          ? 'bg-white border-l-green-500' 
                          : 'bg-yellow-50 border-l-yellow-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-neutral-700">
                          PREGUNTA {index + 1}:
                        </span>
                        {respuesta ? (
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-medium text-sm">Respondida</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-yellow-700">
                            <XCircle className="w-4 h-4" />
                            <span className="font-medium text-sm">Sin responder</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={() => setShowPrevisualizacion(false)}
              disabled={enviando}
            >
              Volver a Revisar
            </Button>
            <Button
              variant="primary"
              onClick={handleEnviarExamen}
              disabled={enviando}
            >
              {enviando ? 'Enviando...' : 'Confirmar Envío'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RealizarExamenRaven;
